import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { req_by_stage, req_mutations } from "@/config/endpoints";

export type RequirementType =
  | "Fields"
  | "Documents"
  | "Custom"
  | "Roles"
  | "User"
  | "Modules"
  | "Tracks"
  | "Notes"
  /**
   * Transition-time gate, the follow-up twin of "Notes": a follow-up must
   * have been scheduled on the entity since its last status change. Like
   * Notes it is specific-only and never appears on the entity checklist.
   */
  | "FollowUps"
  /** "at least N deals" — the minimum rides in `referenceId` as a COUNT. */
  | "DealCount";

/**
 * hierarchy — cumulative (original behaviour): enforced for the gate's status
 *             and everything past it on forward moves.
 * specific  — enforced ONLY when a move lands exactly on the gate's status.
 */
export type RequirementApplyMode = "hierarchy" | "specific";

export interface Requirement {
  id: number;
  type: RequirementType;
  stageId: number;
  statusId: number;
  /**
   * SUB-STATUS NARROWING (2026-09-29). OPTIONAL: null means the requirement
   * covers the WHOLE status — the original behaviour and what every row
   * created before this field carries. Set, the gate only applies when a move
   * lands on that one sub-status.
   */
  subStatusId?: number | null;
  referenceId: number | null;
  referenceKey: string;
  errorMessage: string;
  isBlocking: boolean;
  /** Info-only: a named checklist entry (Documents only) — never blocks, never warns. */
  isInfo?: boolean;
  applyMode?: RequirementApplyMode;
  /**
   * ROLE EXCEPTIONS: the requirement applies to every role EXCEPT these. A
   * move by a user holding one of these roles skips the gate, and that
   * user's checklist / required-field stars omit it. Empty = everyone.
   */
  excludedRoleIds?: number[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateRequirementPayload {
  type: RequirementType;
  referenceKey: string;
  referenceId: number | null;
  stageId: number;
  statusId: number;
  /** null / omitted = the whole status (see `Requirement.subStatusId`). */
  subStatusId?: number | null;
  errorMessage?: string;
  isBlocking?: boolean;
  isInfo?: boolean;
  applyMode?: RequirementApplyMode;
  excludedRoleIds?: number[];
}

export default function useRequirementsByStage(stageId: number | undefined) {
  const query = useQuery<Requirement[]>({
    queryKey: ["requirements", stageId],
    queryFn: async () => {
      const res = await axiosInstance.get(`${req_by_stage}?stageId=${stageId}`);
      return res.data?.requirements ?? [];
    },
    enabled: !!stageId,
  });

  return {
    requirements: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useRequirementsByLeadOrChild(
  id: number,
  type: "lead" | "child"
) {
  const query = useQuery<Requirement[]>({
    queryKey: ["requirements", id, type],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `${req_mutations}/by-entity?type=${type}&id=${id}`
      );
      return res.data?.requirements ?? [];
    },
    enabled: !!id,
  });

  const requirements = query.data ?? [];

  return {
    requirements,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Every DOCUMENT requirement name configured in the workspace, deduped and
 * sorted -- the tenant's own list of "documents we ask a lead for"
 * (Stage & Status -> Requirements, type `Documents`).
 *
 * The same name is usually configured on several stage/status rows; what
 * identifies the document is the `referenceKey`, which is exactly what the
 * requirement validator matches an uploaded file against
 * (`DocumentChecker`: `Documents.name === referenceKey`). So the list is keyed
 * on the name, not on requirement rows -- picking one in an automation stays
 * valid even if the stage gate that introduced it is later removed.
 */
export function useDocumentRequirementNames() {
  const query = useQuery<Requirement[]>({
    queryKey: ["requirements", "all"],
    queryFn: async () => {
      const res = await axiosInstance.get(req_mutations);
      return res.data?.requirements ?? [];
    },
  });

  const names = Array.from(
    new Set(
      (query.data ?? [])
        .filter((r) => r?.type === "Documents")
        .map((r) => String(r.referenceKey ?? "").trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  return { names, isLoading: query.isLoading, error: query.error };
}
